import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { userFacingApiError } from "./errors";

type AsyncAction = () => void | Promise<void>;
type AsyncValueAction = (value: string) => void | Promise<void>;
type ErrorFormatter = (caught: unknown) => string;

type BaseRequest = {
  title: string;
  description?: string;
  callout?: string;
  cancelLabel?: string;
  errorFallback?: string;
  formatError?: ErrorFormatter;
};

export type ConfirmRequest = BaseRequest & {
  confirmLabel?: string;
  danger?: boolean;
  onConfirm?: AsyncAction;
};

export type ReasonRequest = BaseRequest & {
  label?: string;
  confirmLabel?: string;
  initialValue?: string;
  minLength?: number;
  placeholder?: string;
  danger?: boolean;
  onSubmit?: AsyncValueAction;
};

export type InputRequest = ReasonRequest & {
  inputType?: "text" | "number" | "date";
};

export type NoticeRequest = Pick<BaseRequest, "title" | "description" | "callout"> & {
  actionLabel?: string;
};

export type DialogApi = {
  confirm(request: ConfirmRequest): Promise<boolean>;
  reason(request: ReasonRequest): Promise<string | null>;
  input(request: InputRequest): Promise<string | null>;
  notice(request: NoticeRequest): Promise<void>;
};

type DialogState =
  | {
      id: number;
      kind: "confirm";
      request: ConfirmRequest;
      resolve: (value: boolean) => void;
      busy: boolean;
      error: string;
    }
  | {
      id: number;
      kind: "reason" | "input";
      request: ReasonRequest | InputRequest;
      resolve: (value: string | null) => void;
      busy: boolean;
      error: string;
      value: string;
    }
  | {
      id: number;
      kind: "notice";
      request: NoticeRequest;
      resolve: () => void;
      busy: false;
      error: string;
    };

const DialogContext = createContext<DialogApi | null>(null);
const modalLayers: string[] = [];
let modalBodyLockCount = 0;
let modalBodyOverflow = "";

const isTopModalLayer = (id: string) => modalLayers.at(-1) === id;

const removeModalLayer = (id: string) => {
  const index = modalLayers.lastIndexOf(id);
  if (index >= 0) modalLayers.splice(index, 1);
};

function focusableElements(root: HTMLElement | null) {
  return Array.from(
    root?.querySelectorAll<HTMLElement>(
      "a[href]:not([hidden]), button:not([disabled]):not([hidden]), input:not([disabled]):not([type='hidden']):not([hidden]), select:not([disabled]):not([hidden]), textarea:not([disabled]):not([hidden]), [tabindex]:not([tabindex='-1']):not([hidden])",
    ) ?? [],
  ).filter((element) => element.getAttribute("aria-hidden") !== "true" && !element.closest("[aria-hidden='true'], [hidden]"));
}

function resolveError(request: BaseRequest, caught: unknown) {
  return request.formatError?.(caught) ?? userFacingApiError(caught, request.errorFallback ?? "Something went wrong. Try again.");
}

export function ModalSurface({
  title,
  titleId,
  description,
  children,
  onClose,
  size = "default",
  busy = false,
  className,
  showHeader = true,
}: {
  title: string;
  titleId?: string;
  description?: string;
  children: ReactNode;
  onClose?: () => void;
  size?: "compact" | "default" | "wide";
  busy?: boolean;
  className?: string;
  showHeader?: boolean;
}) {
  const generatedTitleId = useId();
  const headingId = titleId ?? generatedTitleId;
  const surfaceRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  useEffect(() => { onCloseRef.current = onClose; busyRef.current = busy; }, [onClose, busy]);

  useEffect(() => {
    modalLayers.push(generatedTitleId);
    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (modalBodyLockCount === 0) modalBodyOverflow = document.body.style.overflow;
    modalBodyLockCount += 1;
    document.body.style.overflow = "hidden";
    const elements = focusableElements(surfaceRef.current);
    elements[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTopModalLayer(generatedTitleId)) return;
      if (event.key === "Escape") {
        if (!busyRef.current) {
          event.preventDefault();
          onCloseRef.current?.();
        }
        return;
      }
      if (event.key !== "Tab") return;
      const current = focusableElements(surfaceRef.current);
      if (!current.length) return;
      const first = current[0];
      const last = current[current.length - 1];
      if (!surfaceRef.current?.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      const wasTop = isTopModalLayer(generatedTitleId);
      removeModalLayer(generatedTitleId);
      modalBodyLockCount = Math.max(0, modalBodyLockCount - 1);
      if (modalBodyLockCount === 0) {
        document.body.style.overflow = modalBodyOverflow;
        if (previouslyFocusedRef.current?.isConnected) previouslyFocusedRef.current.focus();
      } else if (wasTop && previouslyFocusedRef.current?.isConnected) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [generatedTitleId]);

  return createPortal(
    <div className="modal-backdrop" data-dialog-layer="true">
      <div
        ref={surfaceRef}
        className={`modal${size === "compact" ? " compact" : ""}${size === "wide" ? " modal-wide" : ""}${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        {...(showHeader ? { "aria-labelledby": headingId } : { "aria-label": title })}
      >
        {showHeader && <div className="card-title">
          <h2 id={headingId}>{title}</h2>
          {onClose && (
            <button type="button" aria-label={`Close ${title}`} onClick={onClose} disabled={busy}>
              ×
            </button>
          )}
        </div>}
        {description && <p className="modal-copy">{description}</p>}
        {children}
      </div>
    </div>,
    document.body,
  );
}

function DialogRenderer({ state, close, update }: { state: DialogState; close: () => void; update: (changes: { busy?: boolean; error?: string; value?: string }) => void }) {
  const value = state.kind === "reason" || state.kind === "input" ? state.value : "";
  const busy = state.busy;
  const error = state.error;

  const submit = async () => {
    if (state.kind === "notice") {
      close();
      return;
    }
    update({ busy: true, error: "" });
    try {
      if (state.kind === "confirm") {
        await state.request.onConfirm?.();
        state.resolve(true);
      } else {
        const trimmed = value.trim();
        const minLength = state.request.minLength ?? 1;
        if (trimmed.length < minLength) {
          update({ error: `Enter at least ${minLength} character${minLength === 1 ? "" : "s"}.` });
          return;
        }
        await state.request.onSubmit?.(trimmed);
        state.resolve(trimmed);
      }
      close();
    } catch (caught) {
      update({ error: resolveError(state.request, caught) });
    } finally {
      update({ busy: false });
    }
  };

  if (state.kind === "notice") {
    return (
      <ModalSurface title={state.request.title} description={state.request.description} onClose={close} size="compact">
        <form onSubmit={(event) => { event.preventDefault(); void submit(); }}>
          {state.request.callout && <p className="callout">{state.request.callout}</p>}
          <div className="form-actions">
            <button type="submit" className="button">
              {state.request.actionLabel ?? "Close"}
            </button>
          </div>
        </form>
      </ModalSurface>
    );
  }

  const request = state.request;
  const inputRequest = state.kind === "confirm" ? undefined : request as ReasonRequest | InputRequest;
  return (
    <ModalSurface
      title={request.title}
      description={request.description}
      onClose={() => {
        if (!busy) close();
      }}
      size="compact"
      busy={busy}
    >
      <form onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        {request.callout && <p className="callout">{request.callout}</p>}
        {state.kind === "confirm" ? null : (
          <label>
            {inputRequest?.label ?? (state.kind === "reason" ? "Reason" : "Value")}
            <input
              type={state.kind === "input" ? (request as InputRequest).inputType ?? "text" : "text"}
              value={value}
              placeholder={inputRequest?.placeholder}
              minLength={inputRequest?.minLength}
              onChange={(event) => update({ value: event.target.value })}
              autoFocus
            />
          </label>
        )}
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={close} disabled={busy}>
            {request.cancelLabel ?? "Cancel"}
          </button>
          <button type="submit" className={request.danger ? "button danger" : "button"} disabled={busy}>
            {busy ? "Saving…" : state.kind === "confirm" ? request.confirmLabel ?? "Confirm" : request.confirmLabel ?? "Continue"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<DialogState[]>([]);
  const idRef = useRef(0);
  const nextId = () => {
    idRef.current += 1;
    return idRef.current;
  };

  const closeDialog = useCallback((id: number) => {
    setStack((current) => {
      const index = current.findIndex((item) => item.id === id);
      const active = index >= 0 ? current[index] : undefined;
      if (!active) return current;
      if (active.kind === "confirm") active.resolve(false);
      else if (active.kind === "reason" || active.kind === "input") active.resolve(null);
      else if (active.kind === "notice") active.resolve();
      return [...current.slice(0, index), ...current.slice(index + 1)];
    });
  }, []);

  const updateDialog = useCallback((id: number, changes: { busy?: boolean; error?: string; value?: string }) => {
    setStack((current) => current.map((item) => item.id === id ? { ...item, ...changes } as DialogState : item));
  }, []);

  const confirm = useCallback((request: ConfirmRequest) => new Promise<boolean>((resolve) => setStack((current) => [...current, { id: nextId(), kind: "confirm", request, resolve, busy: false, error: "" }])), []);
  const reason = useCallback((request: ReasonRequest) => new Promise<string | null>((resolve) => setStack((current) => [...current, { id: nextId(), kind: "reason", request, resolve, busy: false, error: "", value: request.initialValue ?? "" }])), []);
  const input = useCallback((request: InputRequest) => new Promise<string | null>((resolve) => setStack((current) => [...current, { id: nextId(), kind: "input", request, resolve, busy: false, error: "", value: request.initialValue ?? "" }])), []);
  const notice = useCallback((request: NoticeRequest) => new Promise<void>((resolve) => setStack((current) => [...current, { id: nextId(), kind: "notice", request, resolve, busy: false, error: "" }])), []);
  const api = useMemo(() => ({ confirm, reason, input, notice }), [confirm, reason, input, notice]);
  const active = stack[stack.length - 1];

  return (
    <DialogContext.Provider value={api}>
      {children}
      {active && <DialogRenderer key={active.id} state={active} close={() => closeDialog(active.id)} update={(changes) => updateDialog(active.id, changes)} />}
    </DialogContext.Provider>
  );
}

export function useDialogs() {
  const value = useContext(DialogContext);
  if (!value) throw new Error("useDialogs must be used inside DialogProvider");
  return value;
}
